/**
 * Scheduling v2 — the tenant booking policy (docs/scheduling/scheduling-v2-architecture.md §4.1).
 *
 * One row per tenant; the backend synthesises defaults when none exists yet, so
 * the GET always resolves to a full `SchedulingPolicyView`.
 */
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPut, operationsClient } from '@/lib/api/client';
import { schedulingV2Endpoints } from '@/lib/api/endpoints';
import { schedulingV2Keys } from '@/lib/queryKeys';
import type {
  BookingSchedulingPolicyView,
  SchedulingPolicyView,
  UpdateSchedulingPolicyInput,
} from '@/types/schedulingV2';

export function useSchedulingPolicy(options: { enabled?: boolean } = {}) {
  return useApiQuery<SchedulingPolicyView>({
    queryKey: schedulingV2Keys.policy(),
    enabled: options.enabled ?? true,
    queryFn: ({ signal }) =>
      apiGet<SchedulingPolicyView>(operationsClient, schedulingV2Endpoints.policy(), { signal }),
  });
}

/**
 * `PUT /v2/scheduling/policy` with the editable fields. The response is the
 * whole view, so it is written straight into the cache (the form re-seeds from
 * it) and the key is invalidated for anything else that read the policy — the
 * arrival-windows section's "Exact time" banner, the New Visit form's
 * Unassigned option.
 */
export function useUpdateSchedulingPolicy() {
  const queryClient = useQueryClient();
  return useApiMutation<SchedulingPolicyView, UpdateSchedulingPolicyInput>({
    mutationFn: (payload) =>
      apiPut<SchedulingPolicyView, UpdateSchedulingPolicyInput>(
        operationsClient,
        schedulingV2Endpoints.policy(),
        payload,
      ),
    onSuccess: (policy) => {
      queryClient.setQueryData(schedulingV2Keys.policy(), policy);
      toast.success('Booking policy saved');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: schedulingV2Keys.policy() });
      void queryClient.invalidateQueries({ queryKey: schedulingV2Keys.policyOverrides() });
    },
  });
}

/** The job form reads only the rule it needs, using booking permission. */
export function useBookingSchedulingPolicy(
  options: { enabled?: boolean; jobTypeId?: string | null } = {},
) {
  return useApiQuery<BookingSchedulingPolicyView>({
    queryKey: schedulingV2Keys.bookingPolicy(options.jobTypeId),
    enabled: options.enabled ?? true,
    queryFn: ({ signal }) =>
      apiGet<BookingSchedulingPolicyView>(operationsClient, schedulingV2Endpoints.bookingPolicy(), {
        signal,
        params: options.jobTypeId ? { jobTypeId: options.jobTypeId } : undefined,
      }),
  });
}
